/**
 * Screen 2 — Email verification.
 *
 * Two arrival paths:
 *   - typed/auto-login: enter the 6-digit code from the email body.
 *   - clicked link: arrives with `?token=...`, we verify automatically.
 *
 * Resend has a 30s cooldown so impatient users don't spam the gateway.
 */

import { api, ApiError } from '../api.js';
import { auth } from '../auth.js';
import { createCodeInput } from '../components/code_input.js';
import { setStatus, statusZone } from '../components/form.js';
import { icon, I } from '../components/icons.js';
import { toast } from '../components/toast.js';
import { navigate } from '../router.js';

const STATUS_ID = 'verify-status';
const RESEND_COOLDOWN_SECONDS = 30;

function escapeHtml(s) {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

async function refreshMeAfterVerify() {
  if (auth.isAuthenticated()) {
    try {
      const me = await api('/me');
      auth.setMe(me);
    } catch { /* user signed out: router redirects */ }
  }
}

function startResendCountdown(button, label) {
  let remaining = RESEND_COOLDOWN_SECONDS;
  button.disabled = true;
  function paint() {
    if (remaining <= 0) {
      button.disabled = false;
      label.textContent = '';
      return;
    }
    const mm = String(Math.floor(remaining / 60));
    const ss = String(remaining % 60).padStart(2, '0');
    label.textContent = `Resend code in ${mm}:${ss}`;
    remaining -= 1;
    setTimeout(paint, 1000);
  }
  paint();
}

export async function renderVerifyEmail(root) {
  const params = new URLSearchParams(window.location.search);
  const linkToken = params.get('token');
  const me = auth.getMe();
  const emailHint = me?.email ?? '';

  if (!linkToken && me?.email_verified) {
    navigate('/');
    return;
  }

  root.innerHTML = `
    <div class="appbar">
      <button type="button" class="appbar-back" id="back-btn" aria-label="Back">‹</button>
      <div class="appbar-title">Verify your email</div>
    </div>

    <div class="pad center">
      <div class="ic violet" style="width:54px;height:54px;border-radius:15px;margin:14px auto 0;">
        ${icon(I.mail, { size: 26 })}
      </div>
      <div style="color:var(--ink-2);font-size:13.5px;font-weight:500;margin:14px 0 16px;line-height:1.5;">
        ${
          emailHint
            ? `We sent a 6-digit code to<br><b style="color:#fff;font-weight:700;">${escapeHtml(emailHint)}</b>`
            : 'We sent a 6-digit code to your email. Enter the code below.'
        }
      </div>

      ${statusZone(STATUS_ID)}

      <form id="code-form" novalidate>
        ${
          emailHint
            ? ''
            : `<label class="field" style="margin-top:12px;text-align:left;">
                <span class="field-label">Email</span>
                <input class="field-input" type="email" name="email" autocomplete="email" required />
              </label>`
        }
        <div id="code-input"></div>

        <button type="submit" class="btn">Verify &amp; continue</button>
        <button type="button" class="btn ghost" id="open-mail">Open email app</button>
        <div class="center" style="margin-top:8px;">
          <span class="muted" id="resend-countdown" style="font-size:12.5px;font-weight:600;"></span>
          <button type="button" class="link-button" id="resend-btn" style="font-size:12.5px;">Resend code</button>
        </div>
      </form>

      <div style="font-size:11.5px;color:var(--mute);font-weight:500;margin-top:14px;line-height:1.5;">
        Play free games before verifying — verification is only needed to play for cash or withdraw.
      </div>
    </div>
  `;

  document.getElementById('back-btn')?.addEventListener('click', () => {
    if (history.length > 1) history.back();
    else navigate('/');
  });
  document.getElementById('open-mail')?.addEventListener('click', () => {
    window.location.href = 'mailto:';
  });

  const codeInput = createCodeInput(document.getElementById('code-input'), {
    onComplete: () => document.getElementById('code-form')?.requestSubmit(),
  });

  if (linkToken) {
    setStatus(STATUS_ID, { message: 'Verifying your link…', kind: 'info' });
    try {
      await api('/auth/verify', { method: 'POST', body: { token: linkToken } });
      await refreshMeAfterVerify();
      setStatus(STATUS_ID, { message: 'Email verified. Welcome to Triviana!', kind: 'success' });
      toast.success('Email verified');
      setTimeout(() => navigate(auth.isAuthenticated() ? '/interests' : '/login'), 800);
      return;
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not verify that link.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  }

  const codeForm = document.getElementById('code-form');
  codeForm.addEventListener('submit', async (e) => {
    e.preventDefault();
    const code = codeInput.value();
    if (code.length !== 6) {
      setStatus(STATUS_ID, { message: 'Enter the full 6-digit code.', kind: 'error' });
      codeInput.focus();
      return;
    }
    const email = emailHint || codeForm.email?.value?.trim();
    if (!email) {
      setStatus(STATUS_ID, { message: 'Enter your email first.', kind: 'error' });
      return;
    }
    setStatus(STATUS_ID, { message: 'Verifying…', kind: 'info' });
    const submitBtn = codeForm.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;
    try {
      await api('/auth/verify', { method: 'POST', body: { email, code } });
      await refreshMeAfterVerify();
      setStatus(STATUS_ID, { message: 'Email verified!', kind: 'success' });
      toast.success('Email verified');
      setTimeout(() => navigate(auth.isAuthenticated() ? '/interests' : '/login'), 800);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not verify.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
      codeInput.clear();
    } finally {
      if (submitBtn) submitBtn.disabled = false;
    }
  });

  const resendBtn = document.getElementById('resend-btn');
  const resendLabel = document.getElementById('resend-countdown');
  startResendCountdown(resendBtn, resendLabel);
  resendBtn?.addEventListener('click', async () => {
    const email = emailHint || codeForm.email?.value?.trim();
    if (!email) {
      setStatus(STATUS_ID, { message: 'Enter your email first.', kind: 'error' });
      return;
    }
    setStatus(STATUS_ID, { message: 'Sending a new code…', kind: 'info' });
    try {
      await api('/auth/resend-verification', { method: 'POST', body: { email } });
      setStatus(STATUS_ID, { message: 'Sent. Check your inbox.', kind: 'success' });
      startResendCountdown(resendBtn, resendLabel);
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Could not resend.';
      setStatus(STATUS_ID, { message: msg, kind: 'error' });
    }
  });

  codeInput.focus();
}
